import type { SlotFelder } from "@gui-creator/gemeinsam";
import { useId } from "react";
import { AktionWahl } from "./AktionWahl.tsx";
import { Abschnitt, type Kontext } from "./gemeinsam.tsx";
import { ItemWahl } from "./ItemWahl.tsx";
import { TextFeld, type PlatzhalterInfo } from "./TextFeld.tsx";

export type FeldAenderung = { [K in keyof SlotFelder]?: SlotFelder[K] | null };

/** Bearbeitet einen Satz Slot-Felder; `basis` zeigt bei Zuständen, was ohne Abweichung gilt. */
export function FelderEditor({
  k,
  felder,
  basis,
  setzen,
  platzhalter,
  mitAktion = true,
}: {
  k: Kontext;
  felder: Partial<SlotFelder>;
  basis?: Partial<SlotFelder>;
  setzen: (a: FeldAenderung) => void;
  platzhalter: PlatzhalterInfo[];
  mitAktion?: boolean;
}) {
  const id = useId();
  const rollen = k.katalog.farbrollen;
  const kursiv = !k.katalog.darstellung.kursivAus;
  const wert = <K extends keyof SlotFelder>(f: K) => felder[f] ?? basis?.[f];
  const bild = k.katalog.bedrockBilder[(wert("symbol") as string | undefined) ?? ""];

  return (
    <>
      <Abschnitt titel="Item">
        <ItemWahl label="Item" wert={wert("symbol")} deaktiviert={!k.darf} onWert={(s) => setzen({ symbol: s })} />
        <div className="feldreihe">
          <div className="feld">
            <label htmlFor={`${id}-anz`}>Anzahl</label>
            <input
              id={`${id}-anz`}
              key={String(wert("anzahl") ?? 1)}
              className="eingabe zahlen"
              defaultValue={String(wert("anzahl") ?? 1)}
              disabled={!k.darf}
              onBlur={(e) => {
                const t = e.target.value.trim();
                const n = Number(t);
                setzen({ anzahl: t === "" || t === "1" ? null : Number.isInteger(n) ? Math.min(64, Math.max(1, n)) : t });
              }}
            />
          </div>
          <label className="schalter">
            <input type="checkbox" checked={!!wert("leuchten")} disabled={!k.darf} onChange={(e) => setzen({ leuchten: e.target.checked ? true : null })} />
            <span>Leuchten</span>
          </label>
        </div>
      </Abschnitt>
      <Abschnitt titel="Text">
        <TextFeld label="Name" wert={wert("name") ?? ""} onWert={(t) => setzen({ name: t === "" ? null : t })} rollen={rollen} platzhalter={platzhalter} vorschau="item" kursivStandard={kursiv} deaktiviert={!k.darf} />
        <TextFeld
          label="Beschreibung"
          mehrzeilig
          wert={(wert("beschreibung") ?? []).join("\n")}
          onWert={(t) => setzen({ beschreibung: t === "" ? null : t.split("\n") })}
          rollen={rollen}
          platzhalter={platzhalter}
          vorschau="lore"
          kursivStandard={kursiv}
          leer="Mehrere Zeilen möglich"
          deaktiviert={!k.darf}
        />
      </Abschnitt>
      {mitAktion && (
        <Abschnitt titel="Aktion">
          <AktionWahl
            katalog={k.katalog}
            pluginId={k.ui.plugin.id}
            aktion={wert("aktion")}
            wert={wert("wert")}
            deaktiviert={!k.darf}
            onAendern={(aktion, w) => setzen({ aktion, wert: w })}
          />
          <TextFeld
            label="Vorher nachfragen"
            wert={wert("bestaetigung") ?? ""}
            onWert={(t) => setzen({ bestaetigung: t === "" ? null : t })}
            rollen={rollen}
            platzhalter={platzhalter}
            leer="Leer lassen für keine Nachfrage"
            hinweis="Java zeigt dann eine Ja/Nein-Truhe, Bedrock ein Ja/Nein-Formular."
            deaktiviert={!k.darf}
          />
        </Abschnitt>
      )}
      <Abschnitt titel="Bedrock" offen={false}>
        <div className="feld">
          <label htmlFor={`${id}-bb`}>Bild im Formular</label>
          <input
            id={`${id}-bb`}
            key={wert("bedrockBild") ?? ""}
            className="eingabe mono"
            defaultValue={wert("bedrockBild") ?? ""}
            placeholder={bild ?? "kein Bild"}
            disabled={!k.darf}
            onBlur={(e) => setzen({ bedrockBild: e.target.value.trim() || null })}
          />
          <span className="feld-hinweis">{bild ? "Leer lassen, dann gilt das Bild aus dem Katalog." : "Der Katalog kennt für dieses Item kein Bedrock-Bild."}</span>
        </div>
      </Abschnitt>
    </>
  );
}
